import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { useCompany } from './CompanyContext';
import { toast } from 'sonner';

const AccountantContext = createContext(null);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AccountantProvider({ children }) {
  const { token, user } = useAuth();
  const { setActiveClient: setCompanyActiveClient } = useCompany();
  const [accountantMode, setAccountantMode] = useState(false);
  const [accountantSettings, setAccountantSettings] = useState(null);
  const [activeClient, setActiveClientState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contextLoading, setContextLoading] = useState(false);

  // Sync active client with CompanyContext
  const setActiveClient = useCallback((client) => {
    setActiveClientState(client);
    setCompanyActiveClient(client);
  }, [setCompanyActiveClient]);

  useEffect(() => {
    if (token && user) {
      fetchAccountantMode();
      fetchClientContext();
    } else {
      setLoading(false);
    }
  }, [token, user]);

  const fetchAccountantMode = async () => {
    try {
      const response = await axios.get(`${API_URL}/accountant/mode`);
      setAccountantSettings(response.data);
      setAccountantMode(response.data.accountant_mode || false);
    } catch (error) {
      console.log('Accountant mode not available');
      setAccountantSettings({
        accountant_mode: false,
        accountant_plan: 'accountant_basic',
        client_count: 0,
        plan_features: {}
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClientContext = async () => {
    try {
      const response = await axios.get(`${API_URL}/accountant/context`);
      if (response.data.active_client) {
        setActiveClient(response.data.active_client);
      } else {
        setActiveClient(null);
      }
    } catch (error) {
      console.log('No active client context');
      setActiveClient(null);
    }
  };

  const toggleAccountantMode = async (enabled) => {
    try {
      const response = await axios.patch(`${API_URL}/accountant/mode`, {
        accountant_mode: enabled
      });
      setAccountantSettings(response.data);
      setAccountantMode(enabled);
      
      // Clear active client when disabling accountant mode
      if (!enabled) {
        setActiveClient(null);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateAccountantSettings = async (settings) => {
    try {
      const response = await axios.patch(`${API_URL}/accountant/mode`, settings);
      setAccountantSettings(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Enter a client's workspace
  const enterClientWorkspace = useCallback(async (clientId) => {
    setContextLoading(true);
    try {
      const response = await axios.post(`${API_URL}/accountant/context/enter/${clientId}`);
      // Include client_details in the context for currency/country info
      const contextWithDetails = {
        ...response.data.context,
        client_details: response.data.client
      };
      setActiveClient(contextWithDetails);
      // Refresh company context to use client's company_id
      if (window.__refreshCompany) await window.__refreshCompany();
      toast.success(`Entered workspace: ${response.data.client.name}`);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter client workspace');
      throw error;
    } finally {
      setContextLoading(false);
    }
  }, []);

  // Exit current client workspace
  const exitClientWorkspace = useCallback(async () => {
    setContextLoading(true);
    try {
      const response = await axios.post(`${API_URL}/accountant/context/exit`);
      setActiveClient(null);
      // Refresh company context to restore accountant's own company
      if (window.__refreshCompany) await window.__refreshCompany();
      toast.success('Exited client workspace');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to exit client workspace');
      throw error;
    } finally {
      setContextLoading(false);
    }
  }, []);

  // Switch directly to another client
  const switchClientWorkspace = useCallback(async (clientId) => {
    setContextLoading(true);
    try {
      const response = await axios.post(`${API_URL}/accountant/context/switch/${clientId}`);
      // Include client_details in the context for currency/country info
      const contextWithDetails = {
        ...response.data.context,
        client_details: response.data.client
      };
      setActiveClient(contextWithDetails);
      toast.success(`Switched to: ${response.data.client.name}`);
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch client');
      throw error;
    } finally {
      setContextLoading(false);
    }
  }, []);

  // Get the effective company ID for API calls
  const getEffectiveCompanyId = useCallback(() => {
    if (accountantMode && activeClient) {
      return activeClient.company_id;
    }
    return user?.company_id;
  }, [accountantMode, activeClient, user]);

  // Branding based on mode
  const branding = accountantMode ? {
    appName: 'BizCore Pro™',
    subtitle: 'Multi-Client Business & Tax Compliance Platform',
    footer: 'by SGK Apps',
    logo: '/bizcore-pro-logo.svg',
    theme: 'pro',
    primaryColor: '#0f172a',
    accentColor: '#d4a84b'
  } : {
    appName: 'BizCore™',
    subtitle: 'Your Core Business & Compliance Partner',
    footer: 'by SGK Apps',
    logo: '/bizcore-logo.svg',
    theme: 'standard',
    primaryColor: '#1e3a5f',
    accentColor: '#d4a84b'
  };

  const value = {
    // State
    accountantMode,
    accountantSettings,
    activeClient,
    loading,
    contextLoading,
    branding,
    
    // Actions
    toggleAccountantMode,
    updateAccountantSettings,
    refreshAccountantMode: fetchAccountantMode,
    refreshClientContext: fetchClientContext,
    
    // Client context switching
    enterClientWorkspace,
    exitClientWorkspace,
    switchClientWorkspace,
    getEffectiveCompanyId,
    
    // Computed
    isInClientContext: !!activeClient,
    currentCompanyId: getEffectiveCompanyId(),
    currentCompanyName: activeClient?.company_name || user?.company_name,
    
    // Plan helpers
    isProPlan: accountantSettings?.accountant_plan === 'accountant_pro',
    isEnterprisePlan: accountantSettings?.accountant_plan === 'accountant_enterprise',
    canAddMoreClients: () => {
      if (!accountantSettings) return false;
      const maxClients = accountantSettings.max_clients;
      if (maxClients === -1) return true;
      return (accountantSettings.client_count || 0) < maxClients;
    },
    hasFeature: (feature) => {
      return accountantSettings?.plan_features?.[feature] || false;
    }
  };

  return (
    <AccountantContext.Provider value={value}>
      {children}
    </AccountantContext.Provider>
  );
}

export function useAccountant() {
  const context = useContext(AccountantContext);
  if (!context) {
    throw new Error('useAccountant must be used within an AccountantProvider');
  }
  return context;
}
